import React, { useState } from "react";
import { Box, Menu, MenuItem } from "@mui/material";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import {
  PlaceOutlined as PinIcon,
  PrecisionManufacturing as MachineIcon,
  WarningAmberRounded as WarningIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { WorkOrder, WorkOrderPriorityCode, WorkOrderStatusCode } from "../../models/models";
import WorkOrderTypeBadge from "../WorkOrderTypeBadge";
import WorkOrderPriorityBadge from "../WorkOrderPriorityBadge";
import QuickActionButtons from "./QuickActionButtons";
import useTranslation from "../../../../shared/hooks/useTranslation";
import dayjs from "dayjs";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

interface TaskListItemProps {
  workOrder: WorkOrder;
  onNavigate: (id: string) => void;
  onStatusChange: (id: string, newStatusId: number, completionNotes?: string, createServiceReport?: boolean) => Promise<void>;
  onRequestCompletion?: (workOrder: WorkOrder) => void;
  canWrite: boolean;
  highlighted?: boolean;
}

// ─── Priority left-border colors (per spec) ─────────────────
const priorityBorderColors: Record<number, string> = {
  [WorkOrderPriorityCode.Critical]: "#DC2626",
  [WorkOrderPriorityCode.High]: "#E8A817",
  [WorkOrderPriorityCode.Normal]: "#3B82F6",
  [WorkOrderPriorityCode.Low]: "#8C9099",
};

// ─── Status badge colors (per spec — matches ServiceReportCard) ─────
const STATUS_BADGE_COLORS: Record<number, { bg: string; text: string; border: string }> = {
  [WorkOrderStatusCode.Assigned]: { bg: "#EBF5FB", text: "#2980B9", border: "#AED6F1" },
  [WorkOrderStatusCode.Scheduled]: { bg: "#F5EEF8", text: "#8E44AD", border: "#D2B4DE" },
  [WorkOrderStatusCode.InProgress]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [WorkOrderStatusCode.OnHold]: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  [WorkOrderStatusCode.Completed]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
};

const STATUS_LABEL_KEYS: Record<number, string> = {
  [WorkOrderStatusCode.Assigned]: "statuses.assigned",
  [WorkOrderStatusCode.Scheduled]: "statuses.scheduled",
  [WorkOrderStatusCode.InProgress]: "statuses.inProgress",
  [WorkOrderStatusCode.OnHold]: "statuses.onHold",
  [WorkOrderStatusCode.Completed]: "statuses.completed",
};

// ─── Due date helpers ────────────────────────────────────────
const getDueDateColor = (dueDate?: string): string => {
  if (!dueDate) return "#9CA3AF";
  const now = dayjs().startOf("day");
  const due = dayjs(dueDate).startOf("day");
  const diff = due.diff(now, "day");
  if (diff < 0) return "#DC2626";    // overdue — red
  if (diff === 0) return "#D4572B";  // today — terracotta
  return "#3B82F6";                  // future — blue
};

const isOverdue = (dueDate?: string, statusId?: number): boolean => {
  if (!dueDate) return false;
  if (statusId && statusId >= 6) return false;
  return dayjs(dueDate).startOf("day").isBefore(dayjs().startOf("day"));
};

// ─── Duration formatter ──────────────────────────────────────
const formatDuration = (minutes?: number): string | null => {
  if (!minutes || minutes <= 0) return null;
  if (minutes >= 60) {
    const h = Math.round(minutes / 60);
    return `~${h}h`;
  }
  return `~${minutes}m`;
};

// ─── Co-assignee display ─────────────────────────────────────
const getCoAssignees = (wo: WorkOrder): string | null => {
  if (!wo.assignees || wo.assignees.length <= 1) return null;
  const others = wo.assignees.slice(1);
  if (others.length === 0) return null;
  const firstName = others[0].userName;
  const parts = firstName.split(" ");
  const short = parts.length > 1 ? `${parts[0]} ${parts[1][0]}.` : firstName;
  if (others.length === 1) return short;
  return `${short} +${others.length - 1}`;
};

const TaskListItem = ({ workOrder, onNavigate, onStatusChange, onRequestCompletion, canWrite, highlighted }: TaskListItemProps) => {
  const { isPhone } = useBreakpoint();
  const { translate } = useTranslation("operations");
  const borderColor = priorityBorderColors[workOrder.priorityCode] || priorityBorderColors[WorkOrderPriorityCode.Normal];
  const overdue = isOverdue(workOrder.dueDate, workOrder.workOrderStatusId);
  const duration = formatDuration(workOrder.estimatedDurationMinutes);
  const coAssignee = getCoAssignees(workOrder);

  // 3-dot overflow menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Build title with machine name suffix
  const titleWithMachine = workOrder.machineName
    ? `${workOrder.title} \u2014 ${workOrder.machineName}`
    : workOrder.title;

  // Location display: "Hall A, Bay 3" or "Plant Bratislava"
  const locationDisplay = workOrder.parentLocationName && workOrder.locationName
    ? `${workOrder.locationName}, ${workOrder.parentLocationName}`
    : workOrder.locationName || workOrder.parentLocationName || null;

  const handleStatusChange = async (newStatusId: number, completionNotes?: string, createServiceReport?: boolean) => {
    await onStatusChange(workOrder.id, newStatusId, completionNotes, createServiceReport);
  };

  // ─── In-progress ambient breathing glow (Option C) ───
  // Applied as box-shadow on the card root. Green (#2D8C4E) design-system
  // success color. Slow 4s pulse with low peak opacity (0.10 ring / 0.08
  // outer blur) to signal activity without reading as alert.
  const isInProgress = workOrder.workOrderStatusId === WorkOrderStatusCode.InProgress;
  const inProgressGlowSx = isInProgress
    ? {
        borderColor: "rgba(45,140,78,0.35)",
        willChange: "box-shadow",
        "@media (prefers-reduced-motion: no-preference)": {
          animation: "inProgressBreathe 4s ease-in-out infinite",
        },
        "@media (prefers-reduced-motion: reduce)": {
          boxShadow:
            "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02), 0 0 0 2px rgba(45,140,78,0.12)",
        },
      }
    : {};

  // ─── In-progress diagonal sheen overlay (Option X — background-position) ───
  // Runs IN ADDITION to the breathing glow above. A narrow diagonal green
  // strip sweeps across the card face from top-left to bottom-right every
  // 5.5s (sweep occupies 0–55%, rest 55–100% for an organic idle gap).
  //
  // Implementation: the overlay Box itself carries a 300%x300% linear-gradient
  // with a bright diagonal band (135deg axis). We animate
  // `background-position` from `100% 0%` (band parked off-screen top-right)
  // to `0% 100%` (band has swept past to bottom-left). This produces the
  // same visual effect as translating an oversized child, but with NO
  // pseudo-element, NO transform, NO compositor layer promotion, and NO
  // oversized rasterization tile — it's pure 2D paint of a gradient on
  // the element's own painted background. Works identically on every
  // browser that supports CSS gradients (every browser we ship to).
  //
  // Why we switched from the prior ::before + translate3d(200%x200%)
  // approach: on Windows Chromium, the oversized translated pseudo-element
  // with `will-change: transform` was rasterizing to an empty tile on some
  // GPU paths (likely tile budget / compositor-layer-drop), making the
  // sheen completely invisible on desktop/tablet/phone-width. Android
  // Chrome rendered it fine because its compositor uses different tile
  // sizes. background-position avoids the entire compositor path.
  //
  // Peak opacity 0.14 — ambient light wash, not a layer over text.
  // Reduced-motion users get no sheen animation (breathing glow still
  // signals).
  const inProgressSheen = isInProgress ? (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
        zIndex: 0,
        backgroundImage:
          "linear-gradient(135deg, transparent 0%, rgba(45,140,78,0) 30%, rgba(45,140,78,0.14) 50%, rgba(45,140,78,0) 70%, transparent 100%)",
        backgroundSize: "300% 300%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "100% 0%",
        "@media (prefers-reduced-motion: no-preference)": {
          animation: "inProgressSheen 5.5s ease-in-out infinite",
        },
      }}
    />
  ) : null;

  if (isPhone) {
    // ─── Phone layout ────────────────────────────────────────
    return (
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: "14px 16px",
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderLeft: `4px solid ${borderColor}`,
          borderRadius: "12px",
          boxShadow: dt.cardShadow,
          ...inProgressGlowSx,
          ...(highlighted && { animation: "rowHighlight 1.5s ease-out" }),
        }}
      >
        {inProgressSheen}
        {/* Header: WO number + pills */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <Box
            onClick={() => onNavigate(workOrder.id)}
            sx={{
              cursor: "pointer",
              fontFamily: dt.fontMono,
              fontSize: "13px",
              fontWeight: 600,
              color: "#3498DB",
              letterSpacing: "0.3px",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {workOrder.number}
          </Box>
          <WorkOrderPriorityBadge priorityCode={workOrder.priorityCode} />
          <WorkOrderTypeBadge typeId={workOrder.workOrderTypeId} typeName={workOrder.workOrderTypeName} />
          {overdue && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                padding: "1px 7px",
                borderRadius: "4px",
                backgroundColor: "rgba(220,38,38,0.10)",
                fontSize: "9px",
                fontWeight: 700,
                fontFamily: dt.font,
                color: "#DC2626",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              <WarningIcon sx={{ fontSize: 10 }} />
              {translate("myTasks.overdue")}
            </Box>
          )}
          {/* Status badge */}
          {STATUS_BADGE_COLORS[workOrder.workOrderStatusId] && STATUS_LABEL_KEYS[workOrder.workOrderStatusId] && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 600,
                borderRadius: "4px",
                padding: "3px 10px",
                cursor: "default",
                userSelect: "none",
                backgroundColor: STATUS_BADGE_COLORS[workOrder.workOrderStatusId].bg,
                color: STATUS_BADGE_COLORS[workOrder.workOrderStatusId].text,
                border: `1px solid ${STATUS_BADGE_COLORS[workOrder.workOrderStatusId].border}`,
                whiteSpace: "nowrap",
              }}
            >
              {translate(STATUS_LABEL_KEYS[workOrder.workOrderStatusId])}
            </Box>
          )}
        </Box>

        {/* Title */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 600,
            color: "#0C1117",
            lineHeight: "20px",
            letterSpacing: "-0.2px",
          }}
        >
          {titleWithMachine}
        </Box>

        {/* Meta row — machine + SN + location + due + duration */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {workOrder.machineName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <MachineIcon sx={{ fontSize: 13, color: "#9CA3AF" }} />
              <Box component="span" sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500, color: "#4A4F57" }}>
                {workOrder.machineName}
              </Box>
              {workOrder.machineSerialNumber && (
                <Box component="span" sx={{ fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 400, color: "#9CA3AF", ml: "4px" }}>
                  SN:{workOrder.machineSerialNumber}
                </Box>
              )}
            </Box>
          )}
          {locationDisplay && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <PinIcon sx={{ fontSize: 13, color: "#9CA3AF" }} />
              <Box component="span" sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 400, color: "#6B7280" }}>
                {locationDisplay}
              </Box>
            </Box>
          )}
          {workOrder.dueDate && (
            <Box sx={{
              fontFamily: dt.fontMono,
              fontSize: "11px",
              fontWeight: 600,
              color: getDueDateColor(workOrder.dueDate),
            }}>
              {translate("myTasks.due")}: {formatUiDate(workOrder.dueDate, "dayMonth")}
            </Box>
          )}
          {duration && (
            <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: "#9CA3AF" }}>
              {duration}
            </Box>
          )}
        </Box>

        {/* Work Instructions — blue accent block (matches SR description/notes pattern) */}
        {workOrder.workInstructions && (
          <Box
            sx={{
              borderLeft: "3px solid #3498DB",
              backgroundColor: "#F4F6F9",
              borderRadius: "8px",
              padding: "10px 12px 10px 16px",
              marginTop: "2px",
            }}
          >
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 700,
                color: "#3498DB",
                textTransform: "uppercase",
                letterSpacing: "1.2px",
                marginBottom: "6px",
              }}
            >
              {translate("myTasks.workInstructionsLabel")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                fontWeight: 400,
                color: "#4A4F57",
                lineHeight: "160%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {workOrder.workInstructions}
            </Box>
            <Box
              onClick={() => onNavigate(workOrder.id)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 600,
                color: "#3498DB",
                marginTop: "8px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {translate("myTasks.showFullInstructions")} &rarr;
            </Box>
            {workOrder.createdByUserName && (
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  color: "#9CA3AF",
                  marginTop: "4px",
                }}
              >
                Written by {workOrder.createdByUserName} &middot; {formatUiDate(workOrder.createdDate)}
              </Box>
            )}
          </Box>
        )}

        {/* Quick actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", marginTop: "4px" }}>
          {canWrite && (
            <QuickActionButtons
              statusId={workOrder.workOrderStatusId}
              onStatusChange={handleStatusChange}
              onRequestCompletion={onRequestCompletion ? () => onRequestCompletion(workOrder) : undefined}
            />
          )}
        </Box>
      </Box>
    );
  }

  // ─── Desktop / Tablet layout ─────────────────────────────────
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        padding: "16px 20px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: "12px",
        boxShadow: dt.cardShadow,
        transition: "all 0.2s ease",
        ...inProgressGlowSx,
        ...(highlighted && { animation: "rowHighlight 1.5s ease-out" }),
        "&:hover": {
          boxShadow: dt.cardShadowHover,
        },
      }}
    >
      {inProgressSheen}
      {/* Row 1: Header — WO number + pills (left) | quick actions + three-dot (right) */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
        {/* Left: WO number + type + priority + overdue pills */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", minWidth: 0 }}>
          <Box
            onClick={() => onNavigate(workOrder.id)}
            sx={{
              cursor: "pointer",
              fontFamily: dt.fontMono,
              fontSize: "13px",
              fontWeight: 600,
              color: "#3498DB",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {workOrder.number}
          </Box>
          <WorkOrderPriorityBadge priorityCode={workOrder.priorityCode} />
          <WorkOrderTypeBadge typeId={workOrder.workOrderTypeId} typeName={workOrder.workOrderTypeName} />
          {overdue && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                padding: "2px 8px",
                borderRadius: "4px",
                backgroundColor: "rgba(220,38,38,0.10)",
                fontSize: "10px",
                fontWeight: 700,
                fontFamily: dt.font,
                color: "#DC2626",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              <WarningIcon sx={{ fontSize: 11 }} />
              {translate("myTasks.overdue")}
            </Box>
          )}
          {/* Status badge */}
          {STATUS_BADGE_COLORS[workOrder.workOrderStatusId] && STATUS_LABEL_KEYS[workOrder.workOrderStatusId] && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 600,
                borderRadius: "4px",
                padding: "3px 10px",
                cursor: "default",
                userSelect: "none",
                backgroundColor: STATUS_BADGE_COLORS[workOrder.workOrderStatusId].bg,
                color: STATUS_BADGE_COLORS[workOrder.workOrderStatusId].text,
                border: `1px solid ${STATUS_BADGE_COLORS[workOrder.workOrderStatusId].border}`,
                whiteSpace: "nowrap",
              }}
            >
              {translate(STATUS_LABEL_KEYS[workOrder.workOrderStatusId])}
            </Box>
          )}
        </Box>

        {/* Right: co-assignee + quick actions + 3-dot menu */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          {coAssignee && (
            <Box sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: "#9CA3AF",
              whiteSpace: "nowrap",
            }}>
              {translate("myTasks.with")} {coAssignee}
            </Box>
          )}
          {canWrite && (
            <QuickActionButtons
              statusId={workOrder.workOrderStatusId}
              onStatusChange={handleStatusChange}
              onRequestCompletion={onRequestCompletion ? () => onRequestCompletion(workOrder) : undefined}
            />
          )}
          {/* 3-dot overflow menu — 32x32, transparent bg, 1px border, 8px radius */}
          <Box
            onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              border: "1px solid #E8E5DF",
              backgroundColor: "transparent",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: "#F2F0EB",
                borderColor: "#D1CCC4",
                "& .wo-overflow-icon": {
                  color: "#D4572B",
                },
              },
            }}
          >
            <MoreVertIcon
              className="wo-overflow-icon"
              sx={{
                fontSize: 18,
                color: "#6B6862",
                transition: "color 0.15s ease",
              }}
            />
          </Box>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                border: "1px solid #E5E7EB",
                mt: "4px",
                "& .MuiMenuItem-root": {
                  fontFamily: dt.font,
                  fontSize: "13px",
                  padding: "8px 16px",
                  color: "#444F57",
                },
              },
            }}
          >
            <MenuItem onClick={() => { onNavigate(workOrder.id); setMenuAnchor(null); }}>
              {translate("myTasks.menuViewDetails")}
            </MenuItem>
            <MenuItem onClick={() => { onNavigate(workOrder.id); setMenuAnchor(null); }}>
              {translate("myTasks.menuEditWorkOrder")}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Row 2: Title */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "16px",
          fontWeight: 600,
          color: "#0C1117",
          lineHeight: "22px",
          letterSpacing: "-0.2px",
        }}
      >
        {titleWithMachine}
      </Box>

      {/* Row 3: Meta row — machine name + SN + location + due date + duration */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
        {workOrder.machineName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <MachineIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
            <Box component="span" sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500, color: "#4A4F57" }}>
              {workOrder.machineName}
            </Box>
            {workOrder.machineSerialNumber && (
              <Box component="span" sx={{ fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 400, color: "#9CA3AF", ml: "4px" }}>
                SN:{workOrder.machineSerialNumber}
              </Box>
            )}
          </Box>
        )}
        {locationDisplay && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <PinIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
            <Box component="span" sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 400, color: "#6B7280" }}>
              {locationDisplay}
            </Box>
          </Box>
        )}
        {workOrder.dueDate && (
          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "12px",
              fontWeight: 600,
              color: getDueDateColor(workOrder.dueDate),
              letterSpacing: "0.2px",
            }}
          >
            {translate("myTasks.due")}: {formatUiDate(workOrder.dueDate, "dayMonth")}
          </Box>
        )}
        {duration && (
          <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", color: "#9CA3AF" }}>
            {duration}
          </Box>
        )}
      </Box>

      {/* Row 4: Work Instructions — blue accent block (matches SR description/notes pattern) */}
      {workOrder.workInstructions && (
        <Box
          sx={{
            borderLeft: "3px solid #3498DB",
            backgroundColor: "#F4F6F9",
            borderRadius: "8px",
            padding: "14px 16px",
            marginTop: "6px",
          }}
        >
          {/* "WORK INSTRUCTIONS" label — blue, uppercase (matches SR SectionLabel) */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 700,
              color: "#3498DB",
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              marginBottom: "8px",
            }}
          >
            {translate("myTasks.workInstructionsLabel")}
          </Box>
          {/* Instructions text — 3-line clamp */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              fontWeight: 400,
              color: "#4A4F57",
              lineHeight: "160%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
          >
            {workOrder.workInstructions}
          </Box>
          {/* "Show full instructions →" link — blue (part of content block) */}
          <Box
            onClick={() => onNavigate(workOrder.id)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              color: "#3498DB",
              marginTop: "10px",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {translate("myTasks.showFullInstructions")} &rarr;
          </Box>
          {/* Attribution */}
          {workOrder.createdByUserName && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                color: "#9CA3AF",
                marginTop: "4px",
              }}
            >
              Written by {workOrder.createdByUserName} &middot; {formatUiDate(workOrder.createdDate)}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TaskListItem;
